import React from 'react';

// Recovery prompt for stories that were saved but lost their place in a book
// (e.g. after a multi-device clobber, now prevented by the merge-on-write fix).
// Lets the user re-link each orphaned chapter to a book with one tap.

const NAVY = '#0f172a';
const SLATE = '#3c4653';
const CREAM = '#fffdf8';
const ORANGE = '#F0A24C';

// Strip the internal markers we embed in story content so the preview reads cleanly.
function cleanSnippet(content) {
  if (!content) return '';
  return content
    .replace(/CelebrationBook:\s*[^\n]+/gi, '')
    .replace(/\[Contributed by [^\]]+\]/gi, '')
    .replace(/\[Photo:[^\]]*\]/gi, '')
    .replace(/\[audio:[^\]]*\]/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function OrphanRecoveryModal({ orphanStories, books, defaultBookId, onRecover, onDismiss }) {
  const [targets, setTargets] = React.useState({}); // storyId -> chosen bookId
  const [done, setDone] = React.useState({});       // storyId -> book title it was added to

  if (!orphanStories || orphanStories.length === 0) return null;

  const bookOptions = (books || []).filter(b => b && b.id);
  const fallbackBookId = defaultBookId || (bookOptions[0] && bookOptions[0].id) || null;
  const getTarget = (storyId) => targets[storyId] || fallbackBookId;

  const handleAdd = (story) => {
    const bookId = getTarget(story.id);
    if (!bookId) return;
    onRecover(story.id, bookId);
    const bk = bookOptions.find(b => b.id === bookId);
    setDone(d => ({ ...d, [story.id]: (bk && bk.title) ? bk.title : 'your book' }));
  };

  const remaining = orphanStories.filter(s => !done[s.id]);

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Recover saved stories">
      <div style={card}>
        <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 2 }}>📖</div>
        <h2 style={heading}>We found a saved story that isn’t in a book yet</h2>
        <p style={subhead}>
          {orphanStories.length === 1
            ? 'This chapter was saved safely, but it lost its place in your book. Want to add it back?'
            : `${orphanStories.length} chapters were saved safely, but lost their place in a book. Add them back below.`}
        </p>

        <div style={listWrap}>
          {orphanStories.map((story) => {
            const snippet = cleanSnippet(story.content);
            const recoveredTo = done[story.id];
            return (
              <div key={story.id} style={row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={rowTitle}>{story.title || 'Untitled chapter'}</div>
                  {snippet ? (
                    <div style={rowSnippet}>
                      {snippet.slice(0, 120)}{snippet.length > 120 ? '…' : ''}
                    </div>
                  ) : null}
                  {story.photoPath ? <div style={photoTag}>📷 Includes a photo</div> : null}
                </div>
                <div style={actionsCol}>
                  {recoveredTo ? (
                    <span style={addedTag}>✓ Added to “{recoveredTo}”</span>
                  ) : (
                    <>
                      {bookOptions.length > 1 && (
                        <select
                          value={getTarget(story.id) || ''}
                          onChange={(e) => setTargets((t) => ({ ...t, [story.id]: e.target.value }))}
                          style={select}
                          aria-label="Choose which book to add this chapter to"
                        >
                          {bookOptions.map((b) => (
                            <option key={b.id} value={b.id}>{b.title || 'Untitled book'}</option>
                          ))}
                        </select>
                      )}
                      <button style={addBtn} onClick={() => handleAdd(story)} disabled={!getTarget(story.id)}>
                        Add to my book
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <button style={dismissBtn} onClick={onDismiss}>
          {remaining.length === 0 ? 'Done' : 'Not now'}
        </button>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, zIndex: 4000,
  background: 'rgba(15, 23, 42, 0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16,
};
const card = {
  background: CREAM, borderRadius: 18, maxWidth: 480, width: '100%',
  boxShadow: '0 24px 60px rgba(0,0,0,0.35)', padding: '24px 22px',
  fontFamily: "'Lora', serif",
};
const heading = {
  fontFamily: "'Lora', serif", fontSize: 20, color: NAVY,
  textAlign: 'center', margin: '0 0 8px', lineHeight: 1.3,
};
const subhead = { fontSize: 14.5, color: SLATE, textAlign: 'center', margin: '0 0 4px', lineHeight: 1.5 };
const listWrap = { maxHeight: '46vh', overflowY: 'auto', margin: '16px 0' };
const row = {
  display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
  padding: '12px 0', borderTop: '1px solid #e8dcc8',
};
const rowTitle = { fontFamily: "'Lora', serif", fontSize: 15, color: NAVY, fontWeight: 600 };
const rowSnippet = { fontSize: 13, color: SLATE, marginTop: 3, lineHeight: 1.4 };
const photoTag = { fontSize: 12, color: '#7c2d12', marginTop: 5 };
const actionsCol = { flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 };
const select = {
  fontFamily: "'Lora', serif", fontSize: 13, padding: '6px 8px',
  borderRadius: 8, border: '1px solid #d4c8b0', background: '#fff', color: NAVY, maxWidth: 150,
};
const addBtn = {
  background: `linear-gradient(135deg, ${ORANGE}, #d97706)`,
  color: '#fff', border: 'none', borderRadius: 999, padding: '9px 18px',
  fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Lora', serif",
  whiteSpace: 'nowrap',
};
const addedTag = { fontSize: 13, color: '#15803d', fontWeight: 600 };
const dismissBtn = {
  display: 'block', width: '100%', marginTop: 8,
  background: 'transparent', border: 'none', color: SLATE,
  fontSize: 14, cursor: 'pointer', fontFamily: "'Lora', serif", padding: '8px',
};
